import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  model,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type RadioCardOrientation = 'vertical' | 'horizontal';

type RadioCardGroupItem = {
  value: () => unknown;
  disabled: () => boolean;
};

let nextUniqueId = 0;

@Component({
  selector: 'ct-lib-radio-card-group',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<ng-content />`,
  styleUrls: ['./radio-card-group.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioCardGroupComponent),
      multi: true,
    },
  ],
  host: {
    '[attr.role]': 'multiselect() ? "group" : "radiogroup"',
    '[class.orientation-vertical]': 'orientation() === "vertical"',
    '[class.orientation-horizontal]': 'orientation() === "horizontal"',
    '[class.multiple]': 'multiselect()',
    '[attr.aria-disabled]': 'isDisabled() || null',
  },
})
export class RadioCardGroupComponent implements ControlValueAccessor {
  readonly value = model<unknown>(null);
  readonly name = input(`ct-radio-card-${nextUniqueId++}`);
  readonly orientation = input<RadioCardOrientation>('vertical');
  readonly disabled = input(false);
  readonly multiselect = input(false);

  private readonly cvaDisabled = signal(false);
  private readonly items = signal<RadioCardGroupItem[]>([]);
  private onChange: (value: unknown) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  readonly isDisabled = computed(() => this.disabled() || this.cvaDisabled());

  /**
   * Selects a card. In multiple mode the value is toggled in the selection.
   * @param {unknown} value Card value
   * @returns {void}
   */
  select(value: unknown): void {
    if (this.isDisabled()) {
      return;
    }
    if (this.multiselect()) {
      const nextValue = this.toggleValue(value);
      this.value.set(nextValue);
      this.onChange(nextValue);
      this.onTouched();
      return;
    }
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  /**
   * Whether the given card value is currently selected.
   * @param {unknown} value Card value
   * @returns {boolean}
   */
  isValueSelected(value: unknown): boolean {
    if (this.multiselect()) {
      return this.selectedValues().includes(value);
    }
    return this.value() === value;
  }

  markTouched(): void {
    this.onTouched();
  }

  registerItem(item: RadioCardGroupItem): () => void {
    this.items.update((items) => [...items, item]);

    return () => {
      this.items.update((items) =>
        items.filter((currentItem) => currentItem !== item),
      );
      this.syncSelectionState();
    };
  }

  syncSelectionState(): void {
    if (this.isDisabled() || this.multiselect()) {
      return;
    }

    const items = this.items();
    if (items.length === 0) {
      return;
    }

    const selectedItem = items.find((item) => item.value() === this.value());
    if (selectedItem && !selectedItem.disabled()) {
      return;
    }

    // Keep an explicit selection while cards are still mounting (@for).
    // Only auto-pick the first option when nothing is selected yet.
    if (this.value() != null) {
      return;
    }

    const firstEnabledItem = items.find((item) => !item.disabled());
    const nextValue = firstEnabledItem?.value() ?? null;

    if (this.value() === nextValue) {
      return;
    }

    this.value.set(nextValue);
    this.onChange(nextValue);
  }

  writeValue(value: unknown): void {
    if (this.multiselect()) {
      this.value.set(Array.isArray(value) ? value : []);
      return;
    }
    this.value.set(value);
    this.syncSelectionState();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled.set(isDisabled);
    this.syncSelectionState();
  }

  /**
   * Returns the current selection as an array in multiple mode.
   * @returns {unknown[]}
   */
  private selectedValues(): unknown[] {
    const value = this.value();
    return Array.isArray(value) ? value : [];
  }

  /**
   * Toggles a value in the current multiple selection.
   * @param {unknown} value Card value
   * @returns {unknown[]}
   */
  private toggleValue(value: unknown): unknown[] {
    const current = this.selectedValues();
    if (current.includes(value)) {
      return current.filter((selected) => selected !== value);
    }
    return [...current, value];
  }
}
